/**
 * AudioStorageService - Service de persistance audio utilisant IndexedDB
 *
 * Permet de stocker les blobs audio de manière persistante pour survivre
 * à la navigation entre pages et aux redémarrages de l'application.
 *
 * Règles de gestion:
 * - Chaque audio est lié à un brouillon via draftId
 * - Limite de 50 MB par brouillon
 * - Durée de vie max: 24 heures
 * - Suppression: transcription réussie, brouillon supprimé/envoyé
 */

import { createLogger } from './Debug';

const DB_NAME = 'VoxiaAudioStorage';
const DB_VERSION = 2; // v2: Added airplaneFiles store for airplane mode items
const STORE_NAME = 'audioFiles';
const AIRPLANE_STORE_NAME = 'airplaneFiles'; // Store for airplane mode audio + OCR blobs
const MAX_STORAGE_PER_DRAFT_MB = 50;
const MAX_AGE_HOURS = 24;
const AIRPLANE_MAX_AGE_HOURS = 72; // Airplane items expire after 72h

class AudioStorageService {
  constructor() {
    this.db = null;
    this.isInitialized = false;
    this.initPromise = null;
    this.log = createLogger('AudioStorageService');
  }

  /**
   * Initialise la base de données IndexedDB
   * @returns {Promise<IDBDatabase>}
   */
  async initDB() {
    // Si déjà initialisé, retourner la db existante
    if (this.isInitialized && this.db) {
      return this.db;
    }

    // Si initialisation en cours, attendre
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = new Promise((resolve, reject) => {
      // Vérifier si IndexedDB est supporté
      if (!window.indexedDB) {
        this.log.warn('IndexedDB non supporté - mode mémoire seule');
        reject(new Error('IndexedDB not supported'));
        return;
      }

      const request = window.indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = (event) => {
        this.log.error('Erreur ouverture IndexedDB:', event.target.error);
        reject(event.target.error);
      };

      request.onsuccess = (event) => {
        this.db = event.target.result;
        this.isInitialized = true;
        this.log.info('IndexedDB initialisé avec succès');
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        // Créer l'object store audio s'il n'existe pas
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'audioId' });

          // Index pour récupérer par brouillon
          store.createIndex('by-draft', 'draftId', { unique: false });

          // Index pour le statut
          store.createIndex('by-status', 'status', { unique: false });

          // Index pour le timestamp (nettoyage)
          store.createIndex('by-timestamp', 'timestamp', { unique: false });

          this.log.info('Object store audio créé avec indexes');
        }

        // Créer l'object store pour items mode avion (audio + OCR)
        if (!db.objectStoreNames.contains(AIRPLANE_STORE_NAME)) {
          const airplaneStore = db.createObjectStore(AIRPLANE_STORE_NAME, { keyPath: 'id' });

          // Index pour le type (audio ou ocr)
          airplaneStore.createIndex('by-type', 'type', { unique: false });

          // Index pour le timestamp (nettoyage)
          airplaneStore.createIndex('by-timestamp', 'timestamp', { unique: false });

          this.log.info('Object store airplane créé avec indexes');
        }
      };
    });

    return this.initPromise;
  }

  /**
   * Vérifie que la DB est prête, sinon l'initialise
   * @returns {Promise<IDBDatabase>}
   */
  async ensureDB() {
    if (!this.isInitialized || !this.db) {
      await this.initDB();
    }
    return this.db;
  }

  /**
   * Sauvegarde un blob audio
   * @param {string} audioId - ID unique de l'audio
   * @param {string} draftId - ID du brouillon associé
   * @param {Blob} blob - Le blob audio
   * @param {Object} metadata - Métadonnées (status, durationSeconds, etc.)
   * @returns {Promise<boolean>}
   */
  async saveAudio(audioId, draftId, blob, metadata = {}) {
    try {
      const db = await this.ensureDB();

      // Vérifier la limite de stockage par brouillon
      const currentUsage = await this.getStorageUsageForDraft(draftId);
      const blobSizeMB = blob.size / (1024 * 1024);

      if (currentUsage + blobSizeMB > MAX_STORAGE_PER_DRAFT_MB) {
        this.log.warn(`Limite de stockage atteinte pour draft ${draftId}: ${currentUsage.toFixed(2)} + ${blobSizeMB.toFixed(2)} > ${MAX_STORAGE_PER_DRAFT_MB} MB`);
        throw new Error(`STORAGE_LIMIT_EXCEEDED: Limite de ${MAX_STORAGE_PER_DRAFT_MB} MB atteinte`);
      }

      return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);

        const record = {
          audioId,
          draftId,
          blob,
          metadata: {
            ...metadata,
            createdAt: metadata.createdAt || Date.now(),
            status: metadata.status || 'pending'
          },
          timestamp: Date.now(),
          size: blob.size
        };

        const request = store.put(record);

        request.onsuccess = () => {
          this.log.info(`Audio ${audioId} sauvegardé pour draft ${draftId} (${blobSizeMB.toFixed(2)} MB)`);
          resolve(true);
        };

        request.onerror = (event) => {
          this.log.error('Erreur sauvegarde audio:', event.target.error);
          reject(event.target.error);
        };
      });
    } catch (error) {
      this.log.error('Erreur saveAudio:', error);
      throw error;
    }
  }

  /**
   * Récupère un audio par son ID
   * @param {string} audioId - ID de l'audio
   * @returns {Promise<Object|null>} - {audioId, draftId, blob, metadata, timestamp}
   */
  async getAudio(audioId) {
    try {
      const db = await this.ensureDB();

      return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readonly');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.get(audioId);

        request.onsuccess = (event) => {
          resolve(event.target.result || null);
        };

        request.onerror = (event) => {
          this.log.error('Erreur getAudio:', event.target.error);
          reject(event.target.error);
        };
      });
    } catch (error) {
      this.log.error('Erreur getAudio:', error);
      return null;
    }
  }

  /**
   * Récupère tous les audios d'un brouillon
   * @param {string} draftId - ID du brouillon
   * @returns {Promise<Array>} - Liste des audios
   */
  async getAudiosByDraft(draftId) {
    try {
      const db = await this.ensureDB();

      return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readonly');
        const store = transaction.objectStore(STORE_NAME);
        const index = store.index('by-draft');
        const request = index.getAll(draftId);

        request.onsuccess = (event) => {
          const results = event.target.result || [];
          this.log.info(`Récupéré ${results.length} audio(s) pour draft ${draftId}`);
          resolve(results);
        };

        request.onerror = (event) => {
          this.log.error('Erreur getAudiosByDraft:', event.target.error);
          reject(event.target.error);
        };
      });
    } catch (error) {
      this.log.error('Erreur getAudiosByDraft:', error);
      return [];
    }
  }

  /**
   * Supprime un audio par son ID
   * @param {string} audioId - ID de l'audio
   * @returns {Promise<boolean>}
   */
  async deleteAudio(audioId) {
    try {
      const db = await this.ensureDB();

      return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.delete(audioId);

        request.onsuccess = () => {
          this.log.info(`Audio ${audioId} supprimé`);
          resolve(true);
        };

        request.onerror = (event) => {
          this.log.error('Erreur deleteAudio:', event.target.error);
          reject(event.target.error);
        };
      });
    } catch (error) {
      this.log.error('Erreur deleteAudio:', error);
      return false;
    }
  }

  /**
   * Supprime tous les audios d'un brouillon
   * @param {string} draftId - ID du brouillon
   * @returns {Promise<number>} - Nombre d'audios supprimés
   */
  async deleteAudiosByDraft(draftId) {
    try {
      const db = await this.ensureDB();

      // D'abord récupérer les IDs des audios à supprimer
      const audios = await this.getAudiosByDraft(draftId);

      if (audios.length === 0) {
        return 0;
      }

      return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        let deletedCount = 0;

        audios.forEach(audio => {
          const request = store.delete(audio.audioId);
          request.onsuccess = () => {
            deletedCount++;
          };
        });

        transaction.oncomplete = () => {
          this.log.info(`${deletedCount} audio(s) supprimé(s) pour draft ${draftId}`);
          resolve(deletedCount);
        };

        transaction.onerror = (event) => {
          this.log.error('Erreur deleteAudiosByDraft:', event.target.error);
          reject(event.target.error);
        };
      });
    } catch (error) {
      this.log.error('Erreur deleteAudiosByDraft:', error);
      return 0;
    }
  }

  /**
   * Met à jour le statut d'un audio
   * @param {string} audioId - ID de l'audio
   * @param {string} status - Nouveau statut (pending, processing, error, completed)
   * @param {string} errorMessage - Message d'erreur optionnel
   * @returns {Promise<boolean>}
   */
  async updateAudioStatus(audioId, status, errorMessage = null) {
    try {
      const db = await this.ensureDB();
      const audio = await this.getAudio(audioId);

      if (!audio) {
        this.log.warn(`Audio ${audioId} non trouvé pour mise à jour statut`);
        return false;
      }

      return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);

        audio.metadata.status = status;
        if (errorMessage) {
          audio.metadata.errorMessage = errorMessage;
        }

        const request = store.put(audio);

        request.onsuccess = () => {
          this.log.info(`Statut audio ${audioId} mis à jour: ${status}`);
          resolve(true);
        };

        request.onerror = (event) => {
          this.log.error('Erreur updateAudioStatus:', event.target.error);
          reject(event.target.error);
        };
      });
    } catch (error) {
      this.log.error('Erreur updateAudioStatus:', error);
      return false;
    }
  }

  /**
   * Calcule la taille totale utilisée par un brouillon
   * @param {string} draftId - ID du brouillon
   * @returns {Promise<number>} - Taille en MB
   */
  async getStorageUsageForDraft(draftId) {
    try {
      const audios = await this.getAudiosByDraft(draftId);
      const totalBytes = audios.reduce((sum, audio) => sum + (audio.size || 0), 0);
      return totalBytes / (1024 * 1024); // Convertir en MB
    } catch (error) {
      this.log.error('Erreur getStorageUsageForDraft:', error);
      return 0;
    }
  }

  /**
   * Supprime les audios de plus de 24 heures
   * @returns {Promise<number>} - Nombre d'audios supprimés
   */
  async cleanupExpiredAudio() {
    try {
      const db = await this.ensureDB();
      const maxAge = MAX_AGE_HOURS * 60 * 60 * 1000; // 24h en ms
      const cutoffTime = Date.now() - maxAge;

      return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const index = store.index('by-timestamp');

        // Récupérer tous les audios plus vieux que 24h
        const range = IDBKeyRange.upperBound(cutoffTime);
        const request = index.openCursor(range);

        let deletedCount = 0;

        request.onsuccess = (event) => {
          const cursor = event.target.result;
          if (cursor) {
            store.delete(cursor.primaryKey);
            deletedCount++;
            cursor.continue();
          }
        };

        transaction.oncomplete = () => {
          if (deletedCount > 0) {
            this.log.info(`Nettoyage: ${deletedCount} audio(s) expirés supprimés`);
          }
          resolve(deletedCount);
        };

        transaction.onerror = (event) => {
          this.log.error('Erreur cleanupExpiredAudio:', event.target.error);
          reject(event.target.error);
        };
      });
    } catch (error) {
      this.log.error('Erreur cleanupExpiredAudio:', error);
      return 0;
    }
  }

  /**
   * Obtient des statistiques de stockage globales
   * @returns {Promise<Object>} - {totalCount, totalSizeMB, byDraft: {draftId: sizeMB}}
   */
  async getStorageStats() {
    try {
      const db = await this.ensureDB();

      return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readonly');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.getAll();

        request.onsuccess = (event) => {
          const audios = event.target.result || [];
          const stats = {
            totalCount: audios.length,
            totalSizeMB: 0,
            byDraft: {}
          };

          audios.forEach(audio => {
            const sizeMB = (audio.size || 0) / (1024 * 1024);
            stats.totalSizeMB += sizeMB;

            if (!stats.byDraft[audio.draftId]) {
              stats.byDraft[audio.draftId] = 0;
            }
            stats.byDraft[audio.draftId] += sizeMB;
          });

          resolve(stats);
        };

        request.onerror = (event) => {
          this.log.error('Erreur getStorageStats:', event.target.error);
          reject(event.target.error);
        };
      });
    } catch (error) {
      this.log.error('Erreur getStorageStats:', error);
      return { totalCount: 0, totalSizeMB: 0, byDraft: {} };
    }
  }

  /**
   * Transfère les audios d'un draftId temporaire vers un nouveau draftId
   * Utilisé quand on sauvegarde un brouillon pour la première fois
   * @param {string} oldDraftId - Ancien ID (peut être null ou 'temp')
   * @param {string} newDraftId - Nouveau ID du brouillon
   * @returns {Promise<number>} - Nombre d'audios transférés
   */
  async transferAudiosToDraft(oldDraftId, newDraftId) {
    try {
      if (!oldDraftId || !newDraftId || oldDraftId === newDraftId) {
        return 0;
      }

      const audios = await this.getAudiosByDraft(oldDraftId);

      if (audios.length === 0) {
        return 0;
      }

      const db = await this.ensureDB();

      return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        let transferredCount = 0;

        audios.forEach(audio => {
          // Mettre à jour le draftId
          audio.draftId = newDraftId;
          const request = store.put(audio);
          request.onsuccess = () => {
            transferredCount++;
          };
        });

        transaction.oncomplete = () => {
          this.log.info(`${transferredCount} audio(s) transféré(s) de ${oldDraftId} vers ${newDraftId}`);
          resolve(transferredCount);
        };

        transaction.onerror = (event) => {
          this.log.error('Erreur transferAudiosToDraft:', event.target.error);
          reject(event.target.error);
        };
      });
    } catch (error) {
      this.log.error('Erreur transferAudiosToDraft:', error);
      return 0;
    }
  }
  // ============================================
  // MÉTHODES POUR ITEMS MODE AVION (Audio + OCR)
  // ============================================

  /**
   * Sauvegarde un blob audio/OCR pour le mode avion
   * Convertit le Blob en ArrayBuffer pour éviter les problèmes de clonage structuré
   * @param {string} id - ID de l'item (ex: 'airplane_audio_123')
   * @param {Blob} blob - Le blob audio ou fichier
   * @param {string} type - 'audio' ou 'ocr'
   * @returns {Promise<boolean>}
   */
  async saveAirplaneItem(id, blob, type) {
    try {
      const db = await this.ensureDB();

      // Convert Blob to ArrayBuffer for reliable storage
      const arrayBuffer = await blob.arrayBuffer();
      const mimeType = blob.type || (type === 'audio' ? 'audio/webm' : 'application/octet-stream');

      this.log.info(`Saving airplane item ${id}: blob size=${blob.size}, arrayBuffer size=${arrayBuffer.byteLength}, mimeType=${mimeType}`);

      return new Promise((resolve, reject) => {
        const transaction = db.transaction([AIRPLANE_STORE_NAME], 'readwrite');
        const store = transaction.objectStore(AIRPLANE_STORE_NAME);

        const record = {
          id,
          arrayBuffer, // Store ArrayBuffer instead of Blob
          mimeType,    // Store MIME type separately
          type,
          timestamp: Date.now(),
          size: blob.size
        };

        const request = store.put(record);

        // Wait for transaction complete instead of just request success
        transaction.oncomplete = () => {
          const sizeMB = (blob.size / (1024 * 1024)).toFixed(2);
          this.log.info(`Item airplane ${id} sauvegardé (${sizeMB} MB, type: ${type}, mimeType: ${mimeType})`);
          resolve(true);
        };

        transaction.onerror = (event) => {
          this.log.error('Erreur sauvegarde item airplane:', event.target.error);
          reject(event.target.error);
        };
      });
    } catch (error) {
      this.log.error('Erreur saveAirplaneItem:', error);
      throw error;
    }
  }

  /**
   * Récupère un item mode avion par son ID
   * Reconvertit l'ArrayBuffer stocké en Blob
   * @param {string} id - ID de l'item
   * @returns {Promise<{id: string, blob: Blob, type: string, timestamp: number, mimeType: string}|null>}
   */
  async getAirplaneItem(id) {
    try {
      const db = await this.ensureDB();

      return new Promise((resolve, reject) => {
        const transaction = db.transaction([AIRPLANE_STORE_NAME], 'readonly');
        const store = transaction.objectStore(AIRPLANE_STORE_NAME);
        const request = store.get(id);

        request.onsuccess = (event) => {
          const result = event.target.result;
          if (!result) {
            resolve(null);
            return;
          }

          // Convert ArrayBuffer back to Blob
          // Handle both old format (blob) and new format (arrayBuffer)
          let blob;
          if (result.arrayBuffer) {
            blob = new Blob([result.arrayBuffer], { type: result.mimeType || 'application/octet-stream' });
            this.log.info(`Retrieved airplane item ${id}: arrayBuffer size=${result.arrayBuffer.byteLength}, blob size=${blob.size}, mimeType=${result.mimeType}`);
          } else if (result.blob) {
            // Legacy format - direct blob storage
            blob = result.blob;
            this.log.info(`Retrieved airplane item ${id} (legacy format): blob size=${blob.size}`);
          } else {
            this.log.error(`Airplane item ${id} has no data`);
            resolve(null);
            return;
          }

          resolve({
            id: result.id,
            blob,
            type: result.type,
            timestamp: result.timestamp,
            mimeType: result.mimeType || blob.type,
            size: result.size
          });
        };

        request.onerror = (event) => {
          this.log.error('Erreur getAirplaneItem:', event.target.error);
          reject(event.target.error);
        };
      });
    } catch (error) {
      this.log.error('Erreur getAirplaneItem:', error);
      return null;
    }
  }

  /**
   * Supprime un item mode avion
   * @param {string} id - ID de l'item
   * @returns {Promise<boolean>}
   */
  async deleteAirplaneItem(id) {
    try {
      const db = await this.ensureDB();

      return new Promise((resolve, reject) => {
        const transaction = db.transaction([AIRPLANE_STORE_NAME], 'readwrite');
        const store = transaction.objectStore(AIRPLANE_STORE_NAME);
        const request = store.delete(id);

        request.onsuccess = () => {
          this.log.info(`Item airplane ${id} supprimé`);
          resolve(true);
        };

        request.onerror = (event) => {
          this.log.error('Erreur deleteAirplaneItem:', event.target.error);
          reject(event.target.error);
        };
      });
    } catch (error) {
      this.log.error('Erreur deleteAirplaneItem:', error);
      return false;
    }
  }

  /**
   * Supprime tous les items mode avion
   * @returns {Promise<number>} - Nombre d'items supprimés
   */
  async deleteAllAirplaneItems() {
    try {
      const db = await this.ensureDB();

      return new Promise((resolve, reject) => {
        const transaction = db.transaction([AIRPLANE_STORE_NAME], 'readwrite');
        const store = transaction.objectStore(AIRPLANE_STORE_NAME);

        // Compter d'abord
        const countRequest = store.count();

        countRequest.onsuccess = () => {
          const count = countRequest.result;
          const clearRequest = store.clear();

          clearRequest.onsuccess = () => {
            this.log.info(`${count} item(s) airplane supprimés`);
            resolve(count);
          };

          clearRequest.onerror = (event) => {
            this.log.error('Erreur clear airplane items:', event.target.error);
            reject(event.target.error);
          };
        };

        countRequest.onerror = (event) => {
          this.log.error('Erreur count airplane items:', event.target.error);
          reject(event.target.error);
        };
      });
    } catch (error) {
      this.log.error('Erreur deleteAllAirplaneItems:', error);
      return 0;
    }
  }

  /**
   * Récupère tous les items mode avion
   * @returns {Promise<Array>}
   */
  async getAllAirplaneItems() {
    try {
      const db = await this.ensureDB();

      return new Promise((resolve, reject) => {
        const transaction = db.transaction([AIRPLANE_STORE_NAME], 'readonly');
        const store = transaction.objectStore(AIRPLANE_STORE_NAME);
        const request = store.getAll();

        request.onsuccess = (event) => {
          const results = event.target.result || [];
          resolve(results);
        };

        request.onerror = (event) => {
          this.log.error('Erreur getAllAirplaneItems:', event.target.error);
          reject(event.target.error);
        };
      });
    } catch (error) {
      this.log.error('Erreur getAllAirplaneItems:', error);
      return [];
    }
  }

  /**
   * Nettoie les items mode avion expirés (> 72h)
   * @returns {Promise<number>} - Nombre d'items supprimés
   */
  async cleanupExpiredAirplaneItems() {
    try {
      const db = await this.ensureDB();
      const maxAge = AIRPLANE_MAX_AGE_HOURS * 60 * 60 * 1000;
      const cutoffTime = Date.now() - maxAge;

      return new Promise((resolve, reject) => {
        const transaction = db.transaction([AIRPLANE_STORE_NAME], 'readwrite');
        const store = transaction.objectStore(AIRPLANE_STORE_NAME);
        const index = store.index('by-timestamp');

        const range = IDBKeyRange.upperBound(cutoffTime);
        const request = index.openCursor(range);

        let deletedCount = 0;

        request.onsuccess = (event) => {
          const cursor = event.target.result;
          if (cursor) {
            store.delete(cursor.primaryKey);
            deletedCount++;
            cursor.continue();
          }
        };

        transaction.oncomplete = () => {
          if (deletedCount > 0) {
            this.log.info(`Nettoyage: ${deletedCount} item(s) airplane expirés supprimés`);
          }
          resolve(deletedCount);
        };

        transaction.onerror = (event) => {
          this.log.error('Erreur cleanupExpiredAirplaneItems:', event.target.error);
          reject(event.target.error);
        };
      });
    } catch (error) {
      this.log.error('Erreur cleanupExpiredAirplaneItems:', error);
      return 0;
    }
  }
}

// Exporter une instance singleton
export const audioStorageService = new AudioStorageService();
export default AudioStorageService;
